import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/shared/lib/cn';
import { useT } from '@/shared/lib/i18n';

const TOTAL_CELLS = 42; // always 6 rows × 7 cols

const THIS_YEAR = new Date().getFullYear();
const YEAR_RANGE = Array.from({ length: 21 }, (_, i) => THIS_YEAR - 10 + i);

function isoToDate(iso: string) { return new Date(iso + 'T00:00:00'); }
function toISO(year: number, month: number, day: number) {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}
function todayISO() {
  const n = new Date();
  return toISO(n.getFullYear(), n.getMonth(), n.getDate());
}

interface CalendarPickerProps {
  value: string;       // "YYYY-MM-DD"
  title: string;
  onChange: (v: string) => void;
  onClose: () => void;
  minDate?: string;    // "YYYY-MM-DD", defaults to today
}

export function CalendarPicker({ value, title, onChange, onClose, minDate }: CalendarPickerProps) {
  const t = useT();
  const MONTHS = [
    t('calendar.months.jan'),
    t('calendar.months.feb'),
    t('calendar.months.mar'),
    t('calendar.months.apr'),
    t('calendar.months.may'),
    t('calendar.months.jun'),
    t('calendar.months.jul'),
    t('calendar.months.aug'),
    t('calendar.months.sep'),
    t('calendar.months.oct'),
    t('calendar.months.nov'),
    t('calendar.months.dec'),
  ];
  const WEEKDAYS = [
    t('calendar.weekdays.mon'),
    t('calendar.weekdays.tue'),
    t('calendar.weekdays.wed'),
    t('calendar.weekdays.thu'),
    t('calendar.weekdays.fri'),
    t('calendar.weekdays.sat'),
    t('calendar.weekdays.sun'),
  ];

  const selected = isoToDate(value);
  const [viewYear, setViewYear] = useState(selected.getFullYear());
  const [viewMonth, setViewMonth] = useState(selected.getMonth());
  const [showYearPicker, setShowYearPicker] = useState(false);

  const min = minDate ? isoToDate(minDate) : null;

  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const rawFirst = new Date(viewYear, viewMonth, 1).getDay();
  const firstWeekday = rawFirst === 0 ? 6 : rawFirst - 1; // Mon=0 … Sun=6
  const trailingEmpty = Math.max(0, TOTAL_CELLS - firstWeekday - daysInMonth);

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear((y) => y - 1); }
    else setViewMonth((m) => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear((y) => y + 1); }
    else setViewMonth((m) => m + 1);
  };

  const handleDay = (day: number) => {
    const iso = toISO(viewYear, viewMonth, day);
    if (min && isoToDate(iso) < min) return;
    onChange(iso);
    onClose();
  };

  const handleYearSelect = (y: number) => {
    setViewYear(y);
    setShowYearPicker(false);
  };

  const today = todayISO();

  return (
    <>
      <motion.div
        className="fixed inset-0 bg-black/30 z-40"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose}
      />
      <motion.div
        className="fixed bottom-0 left-0 right-0 z-50 bg-[#eef5ec] rounded-t-[24px] overflow-hidden"
        initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 30, stiffness: 340 }}
      >
        {/* Drag handle */}
        <div className="w-10 h-[5px] bg-black/15 rounded-full mx-auto mt-[10px] mb-1" />

        {/* Header */}
        <div className="flex items-center justify-between px-4 pt-1 pb-2">
          <span className="text-[17px] font-bold">{title}</span>
          <button className="text-brand-teal text-[16px] font-semibold py-1" onClick={onClose}>
            {t('common.done')}
          </button>
        </div>

        {/* Month / year navigation */}
        <div className="flex items-center px-3 pb-2">
          {!showYearPicker && (
            <button
              onClick={prevMonth}
              className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-black/5 active:bg-black/10 flex-shrink-0"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="m15 18-6-6 6-6" />
              </svg>
            </button>
          )}

          <button
            onClick={() => setShowYearPicker((v) => !v)}
            className="flex-1 flex items-center justify-center gap-1.5 py-1 rounded-[10px] hover:bg-black/5 active:bg-black/10 transition-colors"
          >
            {!showYearPicker && (
              <span className="text-[16px] font-semibold">{MONTHS[viewMonth]}</span>
            )}
            <span className="text-[16px] font-semibold text-brand-teal">{viewYear}</span>
            <svg
              width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
              className={cn('text-text-muted transition-transform', showYearPicker && 'rotate-180')}
            >
              <path d="m6 9 6 6 6-6" />
            </svg>
          </button>

          {!showYearPicker && (
            <button
              onClick={nextMonth}
              className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-black/5 active:bg-black/10 flex-shrink-0"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="m9 18 6-6-6-6" />
              </svg>
            </button>
          )}
        </div>

        {/* Year picker or month grid */}
        <AnimatePresence mode="wait" initial={false}>
          {showYearPicker ? (
            <motion.div
              key="years"
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
              transition={{ duration: 0.15 }}
              className="grid grid-cols-4 gap-2 px-3 pb-10 overflow-y-auto liquid-scrollbar max-h-[288px]"
            >
              {YEAR_RANGE.map((y) => (
                <button
                  key={y}
                  onClick={() => handleYearSelect(y)}
                  className={cn(
                    'py-[10px] rounded-[12px] text-[15px] font-semibold transition-all',
                    y === viewYear
                      ? 'bg-brand-gradient text-white shadow-[0_4px_10px_rgba(18,120,90,0.28)]'
                      : 'bg-white/55 border border-white/60 text-text-primary hover:bg-white/80',
                  )}
                >
                  {y}
                </button>
              ))}
            </motion.div>
          ) : (
            <motion.div
              key="days"
              initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.15 }}
            >
              {/* Weekday headers */}
              <div className="grid grid-cols-7 px-3 mb-0.5">
                {WEEKDAYS.map((d) => (
                  <div key={d} className="text-center text-[12px] font-semibold text-text-muted py-1">{d}</div>
                ))}
              </div>

              {/* Fixed 6-row day grid */}
              <div className="grid grid-cols-7 px-3 pb-10">
                {Array.from({ length: firstWeekday }).map((_, i) => (
                  <div key={`pre${i}`} className="h-10" />
                ))}
                {Array.from({ length: daysInMonth }).map((_, i) => {
                  const day = i + 1;
                  const iso = toISO(viewYear, viewMonth, day);
                  const isSelected = iso === value;
                  const isToday = iso === today;
                  const isDisabled = min ? isoToDate(iso) < min : false;
                  return (
                    <button
                      key={day}
                      disabled={isDisabled}
                      onClick={() => handleDay(day)}
                      className={cn(
                        'mx-auto w-10 h-10 rounded-full flex items-center justify-center text-[15px] transition-all',
                        isSelected && 'bg-brand-gradient text-white font-semibold shadow-[0_4px_10px_rgba(18,120,90,0.3)]',
                        !isSelected && isToday && 'font-bold text-brand-teal ring-1 ring-brand-green/50',
                        !isSelected && !isToday && !isDisabled && 'hover:bg-black/5 active:bg-black/10',
                        isDisabled && 'opacity-25 cursor-not-allowed',
                      )}
                    >
                      {day}
                    </button>
                  );
                })}
                {Array.from({ length: trailingEmpty }).map((_, i) => (
                  <div key={`post${i}`} className="h-10" />
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </>
  );
}
